import {
  type ApiError,
  type ChallengeRequest,
  type ChangePasswordRequest,
  type EmailLoginRequest,
  type GoogleLoginRequest,
  LANGUAGE_HEADER,
  LOGIN_ROUTES,
  type PasswordLoginRequest,
  readApiErrors,
} from './contract'
import { isRecord } from './is.record'

/** Outcome of a login request. `errors` holds the API's errors; it is empty for other failures. */
export type LoginResult = { ok: true } | { ok: false; errors: ApiError[] }

/** Outcome of a request that answers with the API's status message. */
export type StatusResult = { ok: true; status: string } | { ok: false; errors: ApiError[] }

/** Browser client of the login routes (see `contract.ts`). */
export interface LoginApi {
  /** Signs in with email and password; the session cookie is set on success. */
  loginWithPassword(request: PasswordLoginRequest): Promise<LoginResult>
  /** Signs in with the token of an emailed login link. */
  loginWithEmailToken(request: EmailLoginRequest): Promise<LoginResult>
  /** Signs in with a Google authorization code. */
  loginWithGoogle(request: GoogleLoginRequest): Promise<LoginResult>
  /** Asks how the user signs in; the status is one of `CHALLENGE`, or another API status. */
  requestChallenge(request: ChallengeRequest): Promise<StatusResult>
  /** Replaces an expired password. */
  changePassword(request: ChangePasswordRequest): Promise<StatusResult>
}

/** Options of {@link createLoginApi}. */
export interface LoginApiOptions {
  /** URL path the login routes are mounted under, e.g. `/api/auth`. */
  basePath: string
  /** Language of the emails the API sends, passed as `x-language`. */
  language?: string
}

/**
 * Creates the browser client of the login routes.
 *
 * Every method resolves; a network failure or an unexpected answer is reported as
 * `{ ok: false, errors: [] }`, so the form can show its generic failure message.
 */
export function createLoginApi({ basePath, language }: LoginApiOptions): LoginApi {
  const post = async (route: string, body: object): Promise<{ ok: boolean; body: unknown }> => {
    try {
      const response = await fetch(`${basePath}/${route}`, {
        body: JSON.stringify(body),
        headers: {
          'content-type': 'application/json',
          ...(language ? { [LANGUAGE_HEADER]: language } : {}),
        },
        method: 'POST',
      })
      return { body: await response.json().catch(() => null), ok: response.ok }
    } catch {
      return { body: null, ok: false }
    }
  }

  const login = async (route: string, body: object): Promise<LoginResult> => {
    const response = await post(route, body)
    return response.ok && isRecord(response.body) && response.body.login === true
      ? { ok: true }
      : {
          errors: readApiErrors(isRecord(response.body) ? response.body.errors : undefined),
          ok: false,
        }
  }

  const status = async (route: string, body: object): Promise<StatusResult> => {
    const response = await post(route, body)
    return response.ok && isRecord(response.body) && typeof response.body.status === 'string'
      ? { ok: true, status: response.body.status }
      : {
          errors: readApiErrors(isRecord(response.body) ? response.body.errors : undefined),
          ok: false,
        }
  }

  return {
    changePassword: (request) => status(LOGIN_ROUTES.changePassword, request),
    loginWithEmailToken: (request) => login(LOGIN_ROUTES.email, request),
    loginWithGoogle: (request) => login(LOGIN_ROUTES.google, request),
    loginWithPassword: (request) => login(LOGIN_ROUTES.password, request),
    requestChallenge: (request) => status(LOGIN_ROUTES.challenge, request),
  }
}
